import { useEffect, useState } from "react";
import {
  formatDuration,
  isTimerFinished,
  timerElapsed,
  timerRemaining,
  type TimerState,
} from "@worktools/shared";
import { updateBlockProps } from "@worktools/shared";
import { useBlockProps } from "../../sync/hooks";
import { useBoard } from "../../sync/BoardContext";

const PRESETS = [5, 15, 25, 45];

export function TimerBlock({ blockId }: { blockId: string }) {
  const { doc } = useBoard();
  const timer = useBlockProps<TimerState>(blockId);
  const [, forceTick] = useState(0);

  useEffect(() => {
    if (timer.startedAt === null) return;
    const id = setInterval(() => forceTick((value) => value + 1), 250);
    return () => clearInterval(id);
  }, [timer.startedAt]);

  const now = Date.now();
  const running = timer.startedAt !== null;
  const finished = isTimerFinished(timer, now);
  const display =
    timer.mode === "countdown" ? timerRemaining(timer, now) : timerElapsed(timer, now);

  const patch = (next: Partial<TimerState>) => updateBlockProps(doc, blockId, next);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
      <input
        type="text"
        value={timer.label ?? ""}
        onChange={(event) => patch({ label: event.target.value })}
        style={{ border: "none", background: "none", padding: 0, fontSize: 12, color: "var(--ink-muted)" }}
      />

      <div
        style={{
          fontSize: 40,
          fontWeight: 300,
          letterSpacing: "-0.02em",
          fontVariantNumeric: "tabular-nums",
          color: finished ? "var(--warn)" : "var(--ink)",
          lineHeight: 1,
        }}
      >
        {formatDuration(display)}
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <button
          className="pill pill--solid"
          onClick={() =>
            running
              ? patch({ startedAt: null, elapsedMs: timerElapsed(timer, Date.now()) })
              : patch({ startedAt: Date.now() })
          }
        >
          {running ? "Pause" : "Start"}
        </button>
        <button className="pill" onClick={() => patch({ startedAt: null, elapsedMs: 0 })}>
          Reset
        </button>
        <button
          className="pill"
          onClick={() =>
            patch({
              mode: timer.mode === "countdown" ? "stopwatch" : "countdown",
              startedAt: null,
              elapsedMs: 0,
            })
          }
        >
          {timer.mode === "countdown" ? "Countdown" : "Stoppuhr"}
        </button>
      </div>

      {timer.mode === "countdown" && (
        <div style={{ display: "flex", gap: 6, marginTop: "auto" }}>
          {PRESETS.map((minutes) => (
            <button
              key={minutes}
              className="pill"
              onClick={() => patch({ durationMs: minutes * 60_000, startedAt: null, elapsedMs: 0 })}
            >
              {minutes}m
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
